import { LocationState, CustomAction, ListView } from "model/state";
import {
  FETCH_LOCATIONS_SUCCESS, SELECT_LOCATION_ROW, SELECT_ALL_LOCATIONS, FETCH_USER_LOCATIONS_SUCCESS,
  UPDATE_LOCATION_SEARCH, FETCH_USER_LOCATIONS_ROLES_SUCCESS, UPDATE_LOCATION_FILTERS, SET_CURRENT_LOCATION,
  SET_LOCATION_SYSTEM_MODE_BULK_SUCCESS, FETCH_USER_LOCATIONS, INCREMENT_LOCATION_PAGE, CREATE_LOCATION_SUCCESS,
  CREATE_LOCATION_ERROR, EDIT_LOCATION_SUCCESS, EDIT_LOCATION_ERROR, REMOVE_LOCATIONS_SUCCESS, REMOVE_LOCATIONS_ERROR,
  ASSIGN_USER_LOCATION_ROLES_SUCCESS, ASSIGN_USER_LOCATION_ROLES_ERROR, REINIT_LOCATION_PAGE_COUNT,
  FETCH_LOCATIONS_FACETS_SUCCESS, FETCH_LOCATIONS_FACETS, UPDATE_SHOW_LOCATION_FILTERS, FETCH_LOCATIONS,
  FETCH_USER_LOCATIONS_ROLES, ADD_LOCATIONS_TO_PARENT_SUCCESS, SET_SINGLE_SELECT_LOCATION, REMOVE_SINGLE_SELECT_LOCATION
} from "constants/ActionTypes";
import { LocationPayload } from "actions";
import { SystemMode } from "model";
import { toPairs, fromPairs } from 'lodash';
import { fromLocationFacetType } from "model/api";

const initialList = {
  rows: [],
  total: 0,
  page: 1
}

const initialFilterState = {
  type: null,
  cities:[],
  states: [],
  countries: [],
  zipCodes: [],
  offlineDevices: false,
}

const initialState: LocationState = {
  list: initialList,
  isAllSelected: false,
  search: '',
  userLocations: initialList,
  parent: null,
  filters: initialFilterState,
  currentLocation: null,
  facets: {},
  showFilters: false,
  fetchingParentId: null,
  singleSelectLocation: null
};

function locationReducer(state: LocationState | undefined = initialState, action: CustomAction<LocationPayload>) {
  switch (action.type) {
    case FETCH_LOCATIONS:
    case FETCH_USER_LOCATIONS_ROLES: {
      return {
        ...state,
        fetchingParentId: action.payload?.parentId || null,
      }
    }

    case FETCH_LOCATIONS_SUCCESS:
    case FETCH_USER_LOCATIONS_ROLES_SUCCESS: {
      // Discard message if does not belong to current location parent (or root)
      if ((action.payload?.parent?.id || null) !== state?.fetchingParentId) {
        return state;
      }

      const newLocations = action.payload?.locations || [];
      const rows = action.payload?.page === 1 ? newLocations : [
        ...(state.list.rows || []),
        ...newLocations,
      ];

      const list = {
        rows,
        total: action.payload?.total || 0,
        page: action.payload?.page || 1
      };
      return {
        ...state,
        list: { ...list },
        parent: action.payload?.parent || null,
        isAllSelected: false,
      }
    }

    case FETCH_USER_LOCATIONS: {
      return {
        ...state,
        userLocations: {
          ...state.userLocations,
          isLoading: true,
        }
      }
    }

    case FETCH_USER_LOCATIONS_SUCCESS: {
      const newRows = action.payload?.locations || [];

      const rows = action.payload?.page === 1 ? newRows : [
        ...(state.userLocations.rows || []),
        ...newRows,
      ];
      return {
        ...state,
        userLocations: {
          rows,
          total: action.payload?.total || initialList.total,
          page: action.payload?.page || initialList.page,
          isLoading: false,
        }
      }
    }

    case SELECT_LOCATION_ROW:
      const index = state.list.rows?.findIndex(loc => loc.id === action.payload?.locationId);
      if (index === undefined) {
        return { ...state }
      }
      const currentLocation = state.list.rows ? state.list.rows[index] : undefined;
      if (currentLocation === undefined) {
        return { ...state }
      }
      const newRows = [
        ...state.list.rows?.slice(0, index) || [],
        {
          ...currentLocation,
          isSelected: action.payload?.select || false
        },
        ...state.list.rows?.slice(index + 1) || []
      ]
      return {
        ...state,
        list: {
          ...state.list,
          rows: newRows
        }
      };

    case SELECT_ALL_LOCATIONS: {

      const newRows = state.list.rows?.map(loc => ({
        ...loc,
        isSelected: action.payload?.selectAll || false
      }))

      return {
        ...state,
        isAllSelected: action.payload?.selectAll || false,
        list: {
          ...state.list,
          rows: newRows || [],
        }
      };
    }

    case UPDATE_LOCATION_SEARCH: {
      return {
        ...state,
        search: action.payload?.search || '',
      }
    }

    case UPDATE_LOCATION_FILTERS: {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload?.filters
        }
      }
    }

    case SET_CURRENT_LOCATION: {
      return {
        ...state,
        currentLocation: action.payload?.currentLocation || null,
      }
    }

    case SET_LOCATION_SYSTEM_MODE_BULK_SUCCESS: {
      const changedLocations = action.payload?.changedLocationIds || [];
      const newRows = state.list.rows?.map(location => ({
        ...location,
        systemMode: {
          ...location.systemMode,
          target: (changedLocations.find(locId => locId === location.id) === undefined ? location.systemMode?.target : action.payload?.newMode) || SystemMode.HOME
        }
      }));

      return {
        ...state,
        list: {
          ...state.list,
          rows: newRows || [],
        }
      };
    }

    case INCREMENT_LOCATION_PAGE: {
      return {
        ...state,
        list: {
          ...state.list,
          page: state.list.page + 1,
        }
      }
    }

    case REINIT_LOCATION_PAGE_COUNT:
    case CREATE_LOCATION_SUCCESS:
    case CREATE_LOCATION_ERROR:
    case EDIT_LOCATION_SUCCESS:
    case EDIT_LOCATION_ERROR:
    case REMOVE_LOCATIONS_SUCCESS:
    case REMOVE_LOCATIONS_ERROR:
    case ASSIGN_USER_LOCATION_ROLES_SUCCESS:
    case ASSIGN_USER_LOCATION_ROLES_ERROR:
      return {
        ...state,
        list: {
          ...state.list,
          page: 1,
        }
      };

    case FETCH_LOCATIONS_FACETS: {
      const facetType = action.payload?.facet?.type;
      const facetTypeStr = facetType !== undefined ? fromLocationFacetType(facetType) : null;

      const allLoading = fromPairs(toPairs(state.facets).map(([facetName, facetValue]: [string, ListView<string>]) => [
        facetName, {
          ...facetValue,
          isLoading: true,
        }
      ]));

      const facets = facetTypeStr !== null ? {
        ...state.facets,
        [facetTypeStr]: { ...state.facets[facetTypeStr], isLoading: true }
      } : allLoading;

      return {
        ...state,
        facets,
      }
    }

    case ADD_LOCATIONS_TO_PARENT_SUCCESS: {
      return {
        ...state,
        list: {
          ...state.list,
          rows: state.list.rows?.filter(loc => !action.payload?.locationIds?.includes(loc.id as string)) || [],
          total: (state.list.total || 0) - (action.payload?.locationIds?.length || 0),
        }
      };
    }

    case FETCH_LOCATIONS_FACETS_SUCCESS: {
      const facets = toPairs(action.payload?.facetValues || {}).reduce((prev, [ind, value]: [string, ListView<string>]) => {
        const newPrev = { ...prev };
        newPrev[ind] = value.page === 1 ? value : {
          ...value,
          rows: [ ...(newPrev[ind].rows || []), ...(value.rows || []) ]
        }
        return newPrev
      }, state.facets);

      return {
        ...state,
        facets,
      }
    }

    case UPDATE_SHOW_LOCATION_FILTERS: {
      return {
        ...state,
        showFilters: action.payload?.show || false,
      }
    }

    case SET_SINGLE_SELECT_LOCATION: {
      return {
        ...state,
        singleSelectLocation: action.payload?.location || null
      };
    }

    case REMOVE_SINGLE_SELECT_LOCATION: {
      return {
        ...state,
        singleSelectLocation: state.singleSelectLocation?.id === action.payload?.location?.id ? null : state.singleSelectLocation
      };
    }

    default:
      return state
  }
}

export default locationReducer;
